import { FormattedMessage, defineMessage } from 'react-intl';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useCallback, useRef } from 'react';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import { Transitions, ScreenElement } from '@micromag/core/components';
import { useScreenSize, useViewerContext, useViewerInteraction, useScreenRenderContext } from '@micromag/core/contexts';
import { useDimensionObserver } from '@micromag/core/hooks';
import { isHeaderFilled, isFooterFilled, getFooterProps, isTextFilled } from '@micromag/core/utils';
import Background from '@micromag/element-background';
import Container from '@micromag/element-container';
import Footer from '@micromag/element-footer';
import Header from '@micromag/element-header';
import Text from '@micromag/element-text';
import Visual from '@micromag/element-visual';

var styles = {"container":"micromag-screen-slideshow-container","placeholder":"micromag-screen-slideshow-placeholder","inner":"micromag-screen-slideshow-inner","background":"micromag-screen-slideshow-background","disabled":"micromag-screen-slideshow-disabled","hidden":"micromag-screen-slideshow-hidden","content":"micromag-screen-slideshow-content","caption":"micromag-screen-slideshow-caption","header":"micromag-screen-slideshow-header","footer":"micromag-screen-slideshow-footer"};

var propTypes = {
  slides: PropTypes.oneOfType([PropTypes$1.imageMedias, PropTypes$1.imageElements]),
  withCaptions: PropTypes.bool,
  spacing: PropTypes.number,
  captionMaxLines: PropTypes.number,
  transitionDelay: PropTypes.number,
  background: PropTypes$1.backgroundElement,
  header: PropTypes$1.header,
  footer: PropTypes$1.footer,
  current: PropTypes.bool,
  active: PropTypes.bool,
  transitions: PropTypes$1.transitions,
  className: PropTypes.string
};
var defaultProps = {
  withCaptions: false,
  slides: [],
  spacing: 20,
  captionMaxLines: 2,
  transitionDelay: 1,
  // in seconds
  background: null,
  header: null,
  footer: null,
  current: true,
  active: true,
  transitions: null,
  className: null
};
var SlideshowScreen = function SlideshowScreen(_ref) {
  var slides = _ref.slides,
    withCaptions = _ref.withCaptions,
    background = _ref.background,
    header = _ref.header,
    footer = _ref.footer,
    current = _ref.current,
    active = _ref.active,
    spacing = _ref.spacing,
    transitionDelay = _ref.transitionDelay,
    captionMaxLines = _ref.captionMaxLines,
    transitions = _ref.transitions,
    className = _ref.className;
  var _useScreenSize = useScreenSize(),
    width = _useScreenSize.width,
    height = _useScreenSize.height,
    resolution = _useScreenSize.resolution;
  var _useViewerContext = useViewerContext(),
    viewerTopHeight = _useViewerContext.topHeight,
    viewerBottomHeight = _useViewerContext.bottomHeight;
  var _useViewerInteraction = useViewerInteraction(),
    enableInteraction = _useViewerInteraction.enableInteraction,
    disableInteraction = _useViewerInteraction.disableInteraction;
  var _useScreenRenderConte = useScreenRenderContext(),
    isView = _useScreenRenderConte.isView,
    isPreview = _useScreenRenderConte.isPreview,
    isPlaceholder = _useScreenRenderConte.isPlaceholder,
    isEdit = _useScreenRenderConte.isEdit,
    isStatic = _useScreenRenderConte.isStatic,
    isCapture = _useScreenRenderConte.isCapture;
  var backgroundPlaying = current && (isView || isEdit);
  var mediaShouldLoad = current || active;
  var finalSpacing = isPlaceholder ? 5 : spacing;
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    imagesLoaded = _useState2[0],
    setImagesLoaded = _useState2[1];
  var ready = true;
  var transitionPlaying = current && ready;
  var transitionDisabled = isStatic || isCapture || isPlaceholder || isPreview;
  var onImageLoaded = useCallback(function () {
    setImagesLoaded(imagesLoaded + 1);
  }, [imagesLoaded, setImagesLoaded]);
  var imagesEl = useRef([]);

  // Head and foot
  var hasHeader = isHeaderFilled(header);
  var hasFooter = isFooterFilled(footer);
  var footerProps = getFooterProps(footer, {
    isView: isView,
    current: current,
    isPreview: isPreview,
    enableInteraction: enableInteraction,
    disableInteraction: disableInteraction
  });
  var _useDimensionObserver = useDimensionObserver(),
    footerRef = _useDimensionObserver.ref,
    _useDimensionObserver2 = _useDimensionObserver.height,
    footerHeight = _useDimensionObserver2 === void 0 ? 0 : _useDimensionObserver2;
  var _useDimensionObserver3 = useDimensionObserver(),
    headerRef = _useDimensionObserver3.ref;
  var items = (slides || []).map(function (item, itemI) {
    var _ref2 = item || {},
      _ref2$media = _ref2.media,
      media = _ref2$media === void 0 ? null : _ref2$media,
      _ref2$caption = _ref2.caption,
      caption = _ref2$caption === void 0 ? null : _ref2$caption;
    var imageSize = {
      width: width,
      height: height
    };
    var hasImage = media !== null;
    var hasCaption = isTextFilled(caption);
    var finalTransitionDelay = itemI > 0 ? itemI * (transitionDelay * 1000) : 0;
    return /*#__PURE__*/React.createElement("div", {
      key: "item-".concat(itemI),
      className: styles.gridItem
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.imageContainer,
      ref: function ref(el) {
        imagesEl.current[itemI] = el;
      }
    }, /*#__PURE__*/React.createElement(Transitions, {
      transitions: transitions,
      delay: finalTransitionDelay,
      playing: transitionPlaying,
      disabled: transitionDisabled,
      fullscreen: true
    }, /*#__PURE__*/React.createElement(ScreenElement, {
      placeholder: "image",
      placeholderProps: {
        className: styles.placeholder,
        height: '100%'
      },
      emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
        id: "ebsPd4",
        defaultMessage: [{
          "type": 0,
          "value": "Image"
        }]
      }),
      emptyClassName: styles.emptyImage,
      isEmpty: !hasImage
    }, mediaShouldLoad ? /*#__PURE__*/React.createElement(Visual, Object.assign({
      className: styles.image,
      media: media
    }, imageSize, {
      resolution: resolution,
      objectFit: {
        fit: 'cover'
      },
      playing: backgroundPlaying,
      active: active,
      withoutVideo: isPreview,
      onLoaded: onImageLoaded
    })) : null))), withCaptions ? /*#__PURE__*/React.createElement(Transitions, {
      transitions: transitions,
      delay: finalTransitionDelay,
      playing: transitionPlaying,
      disabled: transitionDisabled
    }, /*#__PURE__*/React.createElement(ScreenElement, {
      placeholder: "line",
      emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
        id: "6tIigB",
        defaultMessage: [{
          "type": 0,
          "value": "Caption"
        }]
      }),
      emptyClassName: styles.emptyCaption,
      isEmpty: !hasCaption
    }, hasCaption ? /*#__PURE__*/React.createElement("div", {
      className: styles.caption
    }, /*#__PURE__*/React.createElement(Text, Object.assign({}, caption, {
      className: styles.captionText,
      lineClamp: captionMaxLines
    }))) : null)) : null);
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty(_defineProperty({}, className, className !== null), styles.isPlaceholder, isPlaceholder)]),
    "data-screen-ready": ready
  }, /*#__PURE__*/React.createElement(Container, {
    width: width,
    height: height,
    className: styles.content
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.inner,
    style: {
      paddingTop: !isPreview ? viewerTopHeight : null,
      paddingBottom: (hasFooter ? footerHeight - finalSpacing : 0) + (current && !isPreview ? viewerBottomHeight : 0)
    }
  }, isPlaceholder ? /*#__PURE__*/React.createElement(ScreenElement, {
    placeholder: "image",
    placeholderProps: {
      className: styles.placeholder,
      height: '100%'
    },
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "ebsPd4",
      defaultMessage: [{
        "type": 0,
        "value": "Image"
      }]
    })
  }) : null, !isPlaceholder && hasHeader ? /*#__PURE__*/React.createElement("div", {
    className: styles.header,
    ref: headerRef,
    style: {
      padding: finalSpacing,
      paddingTop: finalSpacing / 2
    }
  }, /*#__PURE__*/React.createElement(Header, header)) : null, items, !isPlaceholder && hasFooter ? /*#__PURE__*/React.createElement("div", {
    className: styles.footer,
    ref: footerRef,
    style: {
      padding: finalSpacing / 2
    }
  }, /*#__PURE__*/React.createElement(Footer, footerProps)) : null)), !isPlaceholder ? /*#__PURE__*/React.createElement(Background, {
    background: background,
    width: width,
    height: height,
    resolution: resolution,
    playing: backgroundPlaying,
    shouldLoad: mediaShouldLoad,
    withoutVideo: isPreview,
    className: styles.background
  }) : null);
};
SlideshowScreen.propTypes = propTypes;
SlideshowScreen.defaultProps = defaultProps;

// import * as transforms from './transforms/index';

var definition = [{
  id: 'slideshow',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "6mX2ya",
      defaultMessage: [{
        "type": 0,
        "value": "Images"
      }]
    }),
    order: 7
  },
  title: defineMessage({
    id: "MhGUaY",
    defaultMessage: [{
      "type": 0,
      "value": "Slideshow"
    }]
  }),
  component: SlideshowScreen,
  fields: [{
    name: 'slides',
    type: 'visuals-with-caption',
    label: defineMessage({
      id: "muYwrB",
      defaultMessage: [{
        "type": 0,
        "value": "Images"
      }]
    })
  }, {
    name: 'transitionDelay',
    type: 'number',
    min: 0,
    "float": true,
    label: defineMessage({
      id: "/6zaOP",
      defaultMessage: [{
        "type": 0,
        "value": "Transition Delay (in seconds)"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
}];

export { SlideshowScreen, definition as default };
